import * as React from 'react';
import {useEffect, useState} from 'react';
import {FormattedMessage, InjectedIntl, InjectedIntlProps, injectIntl} from 'react-intl';
import {Form, Formik, FormikProps} from "formik";
import * as Yup from 'yup';
import viaForSchools from "../../assets/viaForSchools.svg";
import {Button, List} from "@material-ui/core";
import QRCode from 'qrcode.react';
import {RouteComponentProps, withRouter} from 'react-router';
import {FormContainer, PageContainer, RowContainer} from '../common/styles';
import * as H from "history";
import {CognitoUser} from "amazon-cognito-identity-js";
import styled from "styled-components";
import {ToastType} from "../../stores/ui-state.store";
import ToastSnackBar from "../common/toastSnackBar";
import viaLogo from "../../assets/viaLogo.svg";
import handleRedirect from '../../utils/redirectHelper';
import {logEvent, MParticleEventName} from "../../utils/m-particle-utils";
import AuthenticationCodeField from "../common/authenticationCodeField";

enum LoginFormFields {
    Totp = 'Totp',
}

interface OwnProps {
    user: any;
    authClient: any;
    isViaForSchools: boolean;
    queryParams: string;
}

interface FormValues {
    [LoginFormFields.Totp]: string;
}

const getValidationSchema = (intl: InjectedIntl) => Yup.object().shape({
    [LoginFormFields.Totp]: Yup.string().matches(/^[0-9]{6}$/, intl.messages['login.totp.code-6-digits'])
        .required(intl.messages['login.totp.required']),
});

const initialValues: FormValues = {
    [LoginFormFields.Totp]: '',
};

type Props = FormikProps<FormValues> & RouteComponentProps & OwnProps & InjectedIntlProps;

const verifyTOTP = async (totpCode: string, user: CognitoUser | any, history: H.History, intl: any,
                          setSubmitting: (isSubmitting: boolean) => void,
                          setShowToast: (isSubmitting: boolean) => void,
                          setMessage: (isSubmitting: string) => void,
                          setToastType: (toastType: ToastType) => void,
                          authClient: any, queryParams: string) => {
    authClient.verifyTotpToken(user, totpCode).then(() => {
        authClient.setPreferredMFA(user, "TOTP")
            .then((res: any) => {
                const username = user.getUsername();
                logEvent(MParticleEventName.MFA_SETUP_SUCCESS, MfaSetup.displayName, {username: username,});
                logEvent(MParticleEventName.LOGIN_SUCCESS, MfaSetup.displayName, {username: username,});
                handleRedirect(history, queryParams);
            }).catch((error: any) => {
            const username = user.getUsername();
            logEvent(MParticleEventName.MFA_SETUP_FAILURE, MfaSetup.displayName,
                {username: username, errorCode: error.code, errorMessage: error.message,});
            logEvent(MParticleEventName.LOGIN_FAILURE, MfaSetup.displayName,
                {username: username, errorCode: error.code, errorMessage: error.message,});
            if (error.code === 'NotAuthorizedException') {
                setMessage(intl.messages['login.totp.session-is-ended']);
                setShowToast(true);
                setSubmitting(false);
            } else if (error.code === 'EnableSoftwareTokenMFAException') {
                setMessage(intl.messages['login.totp.code-not-right']);
                setShowToast(true);
                setSubmitting(false);
            }
        });
    }).catch((error: any) => {
        setToastType(ToastType.ERROR);
        logEvent(MParticleEventName.MFA_SETUP_FAILURE, MfaSetup.displayName,
            {username: user.getUsername(), errorCode: error.code, errorMessage: error.message,});
        logEvent(MParticleEventName.LOGIN_FAILURE, MfaSetup.displayName,
            {username: user.getUsername(), errorCode: error.code, errorMessage: error.message,});
        if (error.code === 'NotAuthorizedException') {
            if (error.message === 'Password attempts exceeded') {
                setMessage('login.via.password-attempts-exceed');
            } else{
                setMessage(intl.messages['login.totp.session-is-ended']);
            }
        } else if (error.code === 'EnableSoftwareTokenMFAException') {
            setMessage(intl.messages['login.totp.code-not-right']);
        } else {
            setMessage('login.via.general-error-message');
        }
        setShowToast(true);
        setSubmitting(false);
    });
};

const MfaSetup: React.FC<Props> = (props) => {
    const { location, history, user, intl,
        authClient, isViaForSchools, queryParams } = props;
    const [showToast, setShowToast] = useState(false);
    const [message, setMessage] = useState(intl.messages['login.via.user-or-password-incorrect']);
    const [toastType, setToastType] = useState(ToastType.SUCCESS);

    // @ts-ignore
    const code = location.state && location.state.code;
    // @ts-ignore
    const username = location.state && location.state.username;
    if (!code || !username || !user.user) {
        history.push('/login');
    }

    const validationSchema = getValidationSchema(intl);

    // @ts-ignore
    const passwordWasSet = location.state && location.state.passwordWasSet;

    useEffect(() => {
        if(passwordWasSet)
        {
            setMessage(intl.messages["login.password-set-message"]);
            setToastType(ToastType.SUCCESS);
            setShowToast(true);
        }
    }, [passwordWasSet, intl.messages]);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={ async (fields, {setSubmitting}) => {
                const totp = fields[LoginFormFields.Totp];
                await verifyTOTP(totp, user.user, history, intl,
                    setSubmitting, setShowToast, setMessage, setToastType, authClient, queryParams);
                setSubmitting(false);
            }}
            render={() => (
                <PageContainer>
                    <FormContainer>
                        <RowContainer style={{backgroundColor: "#F3F5F8", margin: 0}}>
                        <div style={{padding: "20px", margin: "auto"}}>
                            { isViaForSchools ? <img alt="" src={viaForSchools}/> : <img alt="" src={viaLogo}/>}
                        </div>
                    </RowContainer>
                        <Form style={{ display: 'flex', flexDirection: 'column' }}>
                            <RowContainer style={{padding: "15px 20px 0px", flexDirection: "column"}}>
                                <span style={{margin: "auto", padding: "10px", color: "#555"}}>
                                    <FormattedMessage id='login.via.mfa-setup'/>
                                </span>
                            </RowContainer>
                            <RowContainer style={{padding: "0px 60px 15px", flexDirection: "column"}}>
                                <span style={{margin: "auto", padding: "10px"}}>
                                    <QRCode
                                        value={`otpauth://totp/AWSCognito:${user.user.email}?secret=${code}&issuer=ViaOpsCenter`}/>
                                </span>
                                <span style={{padding: "0px 20px"}}>
                                    <List
                                        dense={true}
                                        component={"ol"}
                                        type={"1"}
                                        style={{listStyleType: "decimal"}}
                                    >
                                        <ListItem>
                                            <BoldText>
                                                <FormattedMessage id='login.via.mfa-setup.download'/>
                                            </BoldText>
                                             <FormattedMessage id='login.via.mfa-setup.authentication-app' />
                                        </ListItem>
                                        <ListItem>
                                            <BoldText>
                                                <FormattedMessage id='login.via.mfa-setup.open'/>
                                            </BoldText>
                                             <FormattedMessage id='login.via.mfa-setup.initiate-qr' />
                                        </ListItem>
                                        <ListItem>
                                            <BoldText>
                                                <FormattedMessage id='login.via.mfa-setup.scan'/>
                                            </BoldText>
                                             <FormattedMessage id='login.via.mfa-setup.qr-image' />
                                        </ListItem>
                                        <ListItem>
                                            <BoldText>
                                                <FormattedMessage id='login.via.mfa-setup.enter'/>
                                            </BoldText>
                                             <FormattedMessage id='login.via.mfa-setup.digits' />
                                        </ListItem>
                                    </List>
                                </span>
                                <AuthenticationCodeField name={LoginFormFields.Totp}
                                                         label={intl.messages['login.totp.field-name']}
                                                         id={"mfa-setup.totp-field"}
                                />
                                <Button type="submit"
                                        style={{width: "100%", marginTop: "25px", marginBottom: "60px", height: "40px",
                                            backgroundColor: "#20B7E9", color: "white"}}>
                                    <FormattedMessage id='login.via.login-button' />
                                </Button>
                            </RowContainer>
                        </Form>
                    </FormContainer>
                    <ToastSnackBar show={showToast} message={message}
                                   toastType={toastType}
                                   onClose={() => {setShowToast(false); }}/>
                </PageContainer>
                )} />
    );
};

const ListItem = styled.li`
  color: #555;
  font-weight: 300;
  font-size: 14px;
  margin-bottom: 8px;
`;

const BoldText = styled.span`
    font-weight: bold;
    margin-right: 4px;
`;

MfaSetup.displayName = 'MfaSetup';
export default withRouter(injectIntl(MfaSetup));
